import { StatusPill } from '../../../ui'
import { ReplyListItem, formatDateTime } from '../commsInboxView/shared'
import { stateTone } from './ConversationTrail'
import { COUNTERPARTY_LABEL, ConversationListItem, STATE_LABEL } from './types'
import styles from './styles.module.scss'

interface Props {
    row: ConversationListItem
    selected: boolean
    onSelect: (id: string | null) => void
    categoryLabel: (key: string | null) => string
    /** Show which mailbox the thread lives in (cross-mailbox lists such as search). */
    showMailbox?: boolean
}

/**
 * One thread in a master list: who, when, subject, where it stands. The
 * outcome pill is the newest email that moves the thread; automated mail
 * (bounces, auto replies, sales) is counted but never sets it.
 */
export const ConversationRow = ({ row: r, selected, onSelect, categoryLabel, showMailbox = false }: Props) => {
    const who = r.lenderName || r.counterpartyAddress || r.latest?.fromEmail || '—'
    const automatedOnly = !r.outcomeCategory && r.inboundCount > 0 && r.automatedCount >= r.inboundCount
    return (
        <li className={`${styles.convItem} ${selected ? styles.convItemActive : ''}`} onClick={() => onSelect(selected ? null : r.id)}>
            <div className={styles.convTop}>
                <span className={styles.convWho} title={r.counterpartyAddress ?? undefined}>
                    {who}
                </span>
                <span className={styles.convWhen}>{formatDateTime(r.lastMessageAt)}</span>
            </div>
            <div className={styles.convSubject}>{r.subject || r.latest?.subject || '(no subject)'}</div>
            {(r.summary || r.latest?.aiSummary) && <div className={styles.convSummary}>{r.summary || r.latest?.aiSummary}</div>}
            <div className={styles.convMeta}>
                <StatusPill size="sm" tone={stateTone(r.state)} dot>
                    {STATE_LABEL[r.state]}
                </StatusPill>
                {r.outcomeCategory ? (
                    <StatusPill size="sm" tone="info" title={`Where the thread stands${r.outcomeAt ? ` · set ${formatDateTime(r.outcomeAt)}` : ''}`}>
                        {categoryLabel(r.outcomeCategory)}
                    </StatusPill>
                ) : automatedOnly ? (
                    <StatusPill size="sm" title="Every email in this thread is automated mail; nothing to action">
                        automated mail only
                    </StatusPill>
                ) : r.inboundCount > 0 ? (
                    <StatusPill size="sm" tone="warn" title="No email in this thread has been categorised yet">
                        not yet categorised
                    </StatusPill>
                ) : null}
                {r.latestCategory && r.latestCategory !== r.outcomeCategory && r.outcomeCategory && (
                    <span className={styles.muted} title="The newest email is automated mail; it does not move the thread">
                        latest: {categoryLabel(r.latestCategory)}
                    </span>
                )}
                {r.claimRef ? (
                    <span className={styles.mono} title="Linked to this client and claim">
                        {r.claimRef}
                    </span>
                ) : (
                    <span className={styles.muted}>{COUNTERPARTY_LABEL[r.counterpartyKind]}</span>
                )}
                <span className={styles.muted}>
                    {r.inboundCount} email{r.inboundCount === 1 ? '' : 's'}
                    {r.outboundCount > 0 ? ` · ${r.outboundCount} from us` : ''}
                    {r.automatedCount > 0 && !automatedOnly ? ` · ${r.automatedCount} automated` : ''}
                    {r.unreadCount > 0 ? ` · ${r.unreadCount} unread` : ''}
                </span>
                {showMailbox && r.mailbox && <span className={styles.muted}>{r.mailbox}</span>}
                {r.latest && r.latest.linkState === 'candidates' && !r.latest.caseId && !r.archived && (
                    <StatusPill size="sm" tone="warn" title="The pipeline found more than one possible client for this email; open it to choose.">
                        which client?
                    </StatusPill>
                )}
            </div>
        </li>
    )
}

interface EmailRowProps {
    row: ReplyListItem
    selected: boolean
    /** Opens this one email (null = close). */
    onSelect: (row: ReplyListItem | null) => void
    categoryLabel: (key: string | null) => string
    showMailbox?: boolean
}

/** One received email in a master list: sender, when, subject, its own category and client. */
export const EmailRow = ({ row: r, selected, onSelect, categoryLabel, showMailbox = false }: EmailRowProps) => {
    const who = r.lenderName || r.clientName || r.fromEmail || '—'
    return (
        <li className={`${styles.convItem} ${selected ? styles.convItemActive : ''}`} onClick={() => onSelect(selected ? null : r)}>
            <div className={styles.convTop}>
                <span className={styles.convWho} title={r.fromEmail}>
                    {who}
                </span>
                <span className={styles.convWhen}>{formatDateTime(r.receivedAt)}</span>
            </div>
            <div className={styles.convSubject}>{r.subject || '(no subject)'}</div>
            {r.aiSummary && <div className={styles.convSummary}>{r.aiSummary}</div>}
            <div className={styles.convMeta}>
                {r.classification ? (
                    <StatusPill size="sm" tone="info">
                        {categoryLabel(r.classification)}
                    </StatusPill>
                ) : (
                    <StatusPill size="sm" tone="warn">
                        not yet categorised
                    </StatusPill>
                )}
                {r.triageState === 'archived' && <StatusPill size="sm">archived</StatusPill>}
                {r.claimRef ? (
                    <span className={styles.mono} title="Linked to this client and claim">
                        {r.claimRef}
                    </span>
                ) : (
                    <span className={styles.muted}>{r.partyType ? r.partyType.replace(/_/g, ' ') : 'no client yet'}</span>
                )}
                {r.attachmentCount > 0 && (
                    <span className={styles.muted}>
                        {r.attachmentCount} attachment{r.attachmentCount === 1 ? '' : 's'}
                    </span>
                )}
                {showMailbox && r.sourceMailbox && <span className={styles.muted}>{r.sourceMailbox}</span>}
            </div>
        </li>
    )
}
